import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { reportCardAPI, masterAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiDownload, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';

const GenerateReportCard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const examId = searchParams.get('exam_id');

  const [exam, setExam] = useState(null);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedStudents, setSelectedStudents] = useState([]);

  useEffect(() => {
    if (examId) {
      fetchData();
    }
  }, [examId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const examsRes = await reportCardAPI.getOfflineExams({ id: examId });

      if (examsRes.data.success && examsRes.data.data.length > 0) {
        const examData = examsRes.data.data[0];
        setExam(examData);
        
        // Fetch students for this class
        const studentsRes = await masterAPI.getStudents({ class_id: examData.class_id });
        if (studentsRes.data.success) {
          setStudents(studentsRes.data.data);
          // Select all students by default
          setSelectedStudents(studentsRes.data.data.map(s => s.id));
        }
      }
    } catch (error) {
      toast.error('Error loading data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedStudents(students.map(s => s.id));
    } else {
      setSelectedStudents([]);
    }
  };

  const handleSelectStudent = (studentId) => {
    setSelectedStudents(prev => {
      if (prev.includes(studentId)) {
        return prev.filter(id => id !== studentId);
      } else {
        return [...prev, studentId];
      }
    });
  };

  const handleGenerateReports = async () => {
    if (selectedStudents.length === 0) {
      toast.error('Please select at least one student');
      return;
    }

    setGenerating(true);
    try {
      const response = await reportCardAPI.generateReportCard({
        exam_id: examId,
        student_ids: selectedStudents
      });

      if (response.data.success) {
        toast.success(response.data.message);
        // Optionally download or view the generated reports
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to generate report cards');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!exam) {
    return (
      <div className="p-4 md:p-6">
        <div className="text-center py-12">
          <p className="text-gray-600">Exam not found</p>
          <button
            onClick={() => navigate('/report-cards/exams')}
            className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Back to Exams
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      <div className="mb-6">
        <button
          onClick={() => navigate('/report-cards/exams')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <FiArrowLeft /> Back to Exams
        </button>
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
          Generate Report Cards - {exam.exam_name}
        </h1>
        <p className="text-sm md:text-base text-gray-600 mt-1">
          Class: {exam.class_name}
        </p>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-6">
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={selectedStudents.length === students.length && students.length > 0}
              onChange={handleSelectAll}
              className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-600"
            />
            <span className="font-medium text-gray-900">
              Select All Students ({selectedStudents.length} / {students.length} selected)
            </span>
          </label>
        </div>

        <div className="divide-y divide-gray-200">
          {students.map(student => (
            <div key={student.id} className="p-4 hover:bg-gray-50">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedStudents.includes(student.id)}
                  onChange={() => handleSelectStudent(student.id)}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-600"
                />
                <div className="flex-1">
                  <div className="font-medium text-gray-900">{student.name}</div>
                  <div className="text-sm text-gray-500">{student.email}</div>
                </div>
              </label>
            </div>
          ))}
        </div>
      </div>

      <div className="flex gap-3">
        <button
          onClick={handleGenerateReports}
          disabled={generating || selectedStudents.length === 0}
          className="flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:bg-gray-300"
        >
          <FiDownload /> {generating ? 'Generating...' : 'Generate Report Cards'}
        </button>
        <button
          type="button"
          onClick={() => navigate('/report-cards/exams')}
          className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
        >
          Cancel
        </button>
      </div>

      {selectedStudents.length > 0 && (
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-3">
            <FiCheckCircle className="text-blue-600 mt-0.5" size={20} />
            <div>
              <h3 className="font-semibold text-blue-900 mb-1">Report Card Generation</h3>
              <p className="text-sm text-blue-700">
                Report cards will be generated for {selectedStudents.length} student(s). 
                The system will calculate grades, ranks, and include attendance data automatically.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GenerateReportCard;
